import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check } from 'lucide-react';

// İki adımlı doğrulama kurulumunun "uygulamaya ekle" bölümü: QR + elle giriş anahtarı.
// Hem giriş ekranında (koyu kart, tone="dark") hem Profil → Hesap Güvenliği çekmecesinde
// (açık zemin) kullanılır. QR her zaman BEYAZ zemin üstündedir — koyu zeminde okunmaz.

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`;

const Steps = styled.ol`
  margin: 0;
  padding-left: 18px;
  width: 100%;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.5;
  color: ${({ $tone, theme }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.75)' : theme.colors.text.primary)};

  li + li { margin-top: 4px; }
`;

const QrBox = styled.div`
  background: #fff;
  padding: 12px;
  border-radius: 8px;
  line-height: 0;
`;

const SecretRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
`;

const Secret = styled.code`
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
  font-size: 13px;
  letter-spacing: 1px;
  padding: 6px 10px;
  border-radius: 6px;
  word-break: break-all;
  text-align: center;
  background: ${({ $tone }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#F5F7FA')};
  color: ${({ $tone, theme }) => ($tone === 'dark' ? '#fff' : theme.colors.text.primary)};
`;

const IconAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  background: transparent;
  color: ${({ $tone, theme }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.7)' : theme.colors.primary.main)};

  &:hover { background: ${({ $tone }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(2, 145, 239, 0.08)')}; }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 12px;
  text-align: center;
  color: ${({ $tone }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#6B7280')};
`;

export default function MfaSetupPanel({ setup, tone = 'light' }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copySecret = () => {
    const raw = (setup?.secret || '').replace(/\s/g, '');
    Promise.resolve(navigator.clipboard?.writeText(raw))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  if (!setup) return null;

  return (
    <Wrap>
      <Steps $tone={tone}>
        <li>{t('mfa.setup.stepInstall')}</li>
        <li>{t('mfa.setup.stepScan')}</li>
        <li>{t('mfa.setup.stepCode')}</li>
      </Steps>
      <QrBox data-testid="mfa-qr">
        <QRCodeSVG value={setup.otpAuthUri} size={168} level="M" />
      </QrBox>
      <Hint $tone={tone}>{t('mfa.setup.manualHint')}</Hint>
      <SecretRow>
        <Secret $tone={tone} data-testid="mfa-secret">{setup.secret}</Secret>
        <IconAction
          type="button"
          $tone={tone}
          onClick={copySecret}
          title={copied ? t('mfa.copied') : t('mfa.copyKey')}
          aria-label={copied ? t('mfa.copied') : t('mfa.copyKey')}
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </IconAction>
      </SecretRow>
    </Wrap>
  );
}
